import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import SettingsPage from './pages/SettingsPage';
import CommunicationsPage from './pages/CommunicationsPage';
import ClientsPage from './pages/clients/ClientsPage';
import ClientsLeadsPage from './pages/clients/ClientsLeadsPage';
import PushNotificationsPage from './pages/clients/PushNotificationsPage';
import ClientFormPage from './pages/clients/ClientFormPage';
import ClientDetailPage from './pages/clients/ClientDetailPage';
import UsersPage from './pages/UsersPage';
import SocialPage from './pages/SocialPage';
import LeadsPage from './pages/LeadsPage';
import AgendaPage from './pages/AgendaPage';
import TasksPage from './pages/TasksPage';
import TaskTimesPage from './pages/TaskTimesPage';
import WorkdayPage from './pages/WorkdayPage';
import CaptacionBusinessPage from './pages/CaptacionBusinessPage';
import CaptacionBormePage from './pages/CaptacionBormePage';
import ObtenerEmailsPage from './pages/ObtenerEmailsPage';
import GoogleAdsPage from './pages/GoogleAdsPage';
import GoogleConnectionsPage from './pages/GoogleConnectionsPage';
import QuickQuotePage from './pages/QuickQuotePage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectTasksPage from './pages/ProjectTasksPage';
import AjustesPage from './pages/AjustesPage';
import AjusteDetailPage from './pages/AjusteDetailPage';
import ProjectFormPage from './pages/ProjectFormPage';
import WhatsappPage from './pages/WhatsappPage';
import WhatsappTemplatesPage from './pages/WhatsappTemplatesPage';
import EmailMarketingPage from './pages/EmailMarketingPage';
import AudienceContactsPage from './pages/AudienceContactsPage';
import GalleryPage from './pages/GalleryPage';
import AIUsagePage from './pages/AIUsagePage';
import ServicesPage from './pages/ServicesPage';
import FacturasPage from './pages/FacturasPage';
import FacturasRecurrentesPage from './pages/FacturasRecurrentesPage';
import PresupuestosPage from './pages/PresupuestosPage';
import InvoiceFormPage from './pages/InvoiceFormPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import BudgetFormPage from './pages/BudgetFormPage';
import DomainsPage from './pages/DomainsPage';
import HostingsPage from './pages/HostingsPage';
import SoftwarePage from './pages/SoftwarePage';
import MonitoringPage from './pages/MonitoringPage';
import AgentesPage from './pages/AgentesPage';
import CustomerLoginPage from './pages/CustomerLoginPage';
import CustomerResetPage from './pages/CustomerResetPage';
import CustomerPortalLayout from './pages/customer/CustomerPortalLayout';
import CustomerDashboardPage from './pages/customer/CustomerDashboardPage';
import CustomerProjectsPage from './pages/customer/CustomerProjectsPage';
import CustomerProjectDetailPage from './pages/customer/CustomerProjectDetailPage';
import CustomerTimePacksPage from './pages/customer/CustomerTimePacksPage';
import CustomerLicensesPage from './pages/customer/CustomerLicensesPage';
import CustomerInvoicesPage from './pages/customer/CustomerInvoicesPage';
import CustomerBudgetsPage from './pages/customer/CustomerBudgetsPage';
import CustomerInfraPage from './pages/customer/CustomerInfraPage';
import CustomerDownloadsPage from './pages/customer/CustomerDownloadsPage';
import CustomerServicesPage from './pages/customer/CustomerServicesPage';
import CustomerProfilePage from './pages/customer/CustomerProfilePage';
import SupportTicketsPage from './pages/SupportTicketsPage';
import SupportTicketDetailPage from './pages/SupportTicketDetailPage';
import CustomerSupportPage from './pages/customer/CustomerSupportPage';
import CustomerSupportDetailPage from './pages/customer/CustomerSupportDetailPage';
import './App.css';

const parseStoredModules = () => {
  try {
    const raw = localStorage.getItem('modules');
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.map((m) => String(m || '').trim().toLowerCase()).filter(Boolean) : null;
  } catch (_err) {
    return null;
  }
};

const hasModuleAccess = (modules, requiredModule) => {
  if (!requiredModule) return true;
  if (!Array.isArray(modules) || modules.length === 0) return true;
  return modules.includes(requiredModule);
};

const ProtectedRoute = ({ isAuthenticated, modules, requiredModule, element }) => {
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!hasModuleAccess(modules, requiredModule)) return <Navigate to="/" replace />;
  return element;
};

// /ajustes/:id → /cambios/:id (el módulo se llamó «Ajustes» hasta el 2026-10-14)
const LegacyAjustesRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/cambios/${id}`} replace />;
};

const App = () => {
  const token = localStorage.getItem('token');
  const isAuthenticated = Boolean(token);
  const modules = parseStoredModules();
  const location = useLocation();
  const isCustomerRoute = location.pathname === '/cliente' || location.pathname.startsWith('/cliente/');
  const isAuthPage = location.pathname === '/login' || isCustomerRoute;
  const withAccess = (element, requiredModule) => (
    <ProtectedRoute
      isAuthenticated={isAuthenticated}
      modules={modules}
      requiredModule={requiredModule}
      element={element}
    />
  );

  return (
    <div className={isAuthenticated && !isAuthPage ? 'app-layout' : ''}>
      {isAuthenticated && !isAuthPage && <Sidebar />}
      <div className={isAuthenticated && !isAuthPage ? 'main-content' : ''}>
        <div className={isAuthenticated && !isAuthPage ? 'container-fluid pt-4' : ''}>
          <Routes>
            <Route path="/" element={withAccess(<DashboardPage />)} />
            <Route path="/clientes" element={withAccess(<ClientsPage />, 'clientes')} />
            <Route path="/clientes/leads" element={withAccess(<ClientsLeadsPage />, 'clientes')} />
            <Route path="/clientes/notificaciones" element={withAccess(<PushNotificationsPage />, 'clientes')} />
            <Route path="/clientes/nuevo" element={withAccess(<ClientFormPage mode="create" />, 'clientes')} />
            <Route path="/clientes/:id/editar" element={withAccess(<ClientFormPage mode="edit" />, 'clientes')} />
            <Route path="/clientes/:id" element={withAccess(<ClientDetailPage />, 'clientes')} />
            <Route path="/comunicaciones" element={withAccess(<CommunicationsPage />, 'comunicaciones')} />
            <Route path="/usuarios" element={withAccess(<UsersPage />, 'usuarios')} />
            <Route path="/social" element={withAccess(<SocialPage />, 'social')} />
            <Route path="/captacion" element={withAccess(<LeadsPage />, 'captacion')} />
            <Route path="/captacion-business" element={withAccess(<CaptacionBusinessPage />, 'captacion_business')} />
            <Route path="/captacion-emails" element={withAccess(<ObtenerEmailsPage />, 'captacion_business')} />
            <Route path="/captacion-borme" element={withAccess(<CaptacionBormePage />, 'captacion_borme')} />
            <Route path="/google-ads" element={withAccess(<GoogleAdsPage />, 'google_ads')} />
            <Route path="/conexiones-google" element={withAccess(<GoogleConnectionsPage />, 'google_api')} />
            <Route path="/presupuesto-1-minuto" element={withAccess(<QuickQuotePage />, 'google_ads')} />
            <Route path="/agenda" element={withAccess(<AgendaPage />, 'agenda')} />
            <Route path="/tareas" element={withAccess(<TasksPage />, 'tareas')} />
            <Route path="/tareas/tiempos" element={withAccess(<TaskTimesPage />, 'tareas')} />
            <Route path="/jornada" element={withAccess(<WorkdayPage />, 'jornada')} />
            <Route path="/proyectos" element={withAccess(<ProjectsPage />, 'proyectos')} />
            <Route path="/proyectos/:id" element={withAccess(<ProjectFormPage />, 'proyectos')} />
            <Route path="/proyectos/:id/tareas" element={withAccess(<ProjectTasksPage />, 'proyectos')} />
            <Route path="/cambios" element={withAccess(<AjustesPage />, 'proyectos')} />
            <Route path="/cambios/:id" element={withAccess(<AjusteDetailPage />, 'proyectos')} />
            {/* Ruta antigua del módulo (notificaciones y enlaces ya enviados) */}
            <Route path="/ajustes" element={<Navigate to="/cambios" replace />} />
            <Route path="/ajustes/:id" element={<LegacyAjustesRedirect />} />
            <Route path="/configuracion" element={withAccess(<SettingsPage />, 'configuracion')} />
            <Route path="/whatsapp" element={withAccess(<WhatsappPage />, 'whatsapp')} />
            <Route path="/whatsapp/qr" element={withAccess(<WhatsappPage />, 'whatsapp')} />
            <Route path="/whatsapp/plantillas" element={withAccess(<WhatsappTemplatesPage />, 'whatsapp')} />
            <Route path="/soporte" element={withAccess(<SupportTicketsPage />, 'soporte')} />
            <Route path="/soporte/:id" element={withAccess(<SupportTicketDetailPage />, 'soporte')} />
            <Route path="/email-marketing" element={withAccess(<EmailMarketingPage />, 'email_marketing')} />
            <Route path="/email-marketing/audiencias/:id" element={withAccess(<AudienceContactsPage />, 'email_marketing')} />
            <Route path="/galeria" element={withAccess(<GalleryPage />, 'galeria')} />
            <Route path="/servicios" element={withAccess(<ServicesPage />, 'services')} />
            <Route path="/facturas/nueva" element={withAccess(<InvoiceFormPage />, 'facturas')} />
            <Route path="/facturas/:id/editar" element={withAccess(<InvoiceFormPage />, 'facturas')} />
            <Route path="/facturas/:id" element={withAccess(<InvoiceDetailPage />, 'facturas')} />
            <Route path="/facturas-recurrentes" element={withAccess(<FacturasRecurrentesPage />, 'facturas')} />
            <Route path="/facturas" element={withAccess(<FacturasPage />, 'facturas')} />
            <Route path="/presupuestos/nuevo" element={withAccess(<BudgetFormPage />, 'presupuestos')} />
            <Route path="/presupuestos/:id" element={withAccess(<BudgetFormPage />, 'presupuestos')} />
            <Route path="/presupuestos" element={withAccess(<PresupuestosPage />, 'presupuestos')} />
            <Route path="/dominios" element={withAccess(<DomainsPage />, 'dominios')} />
            <Route path="/hosting" element={withAccess(<HostingsPage />, 'hosting')} />
            <Route path="/software" element={withAccess(<SoftwarePage />, 'software')} />
            <Route path="/monitorizacion" element={withAccess(<MonitoringPage />, 'monitorizacion')} />
            <Route path="/agentes" element={withAccess(<AgentesPage />, 'agentes')} />
            <Route path="/logs-ia" element={withAccess(<AIUsagePage />, 'configuracion')} />

            <Route path="/cliente/login" element={<CustomerLoginPage />} />
            <Route path="/cliente/reset" element={<CustomerResetPage />} />
            <Route path="/cliente" element={<CustomerPortalLayout />}>
              <Route index element={<CustomerDashboardPage />} />
              <Route path="proyectos" element={<CustomerProjectsPage />} />
              <Route path="proyectos/:id" element={<CustomerProjectDetailPage />} />
              <Route path="bolsas-de-horas" element={<CustomerTimePacksPage />} />
              <Route path="licencias" element={<CustomerLicensesPage />} />
              <Route path="facturas" element={<CustomerInvoicesPage />} />
              <Route path="presupuestos" element={<CustomerBudgetsPage />} />
              <Route path="infraestructura" element={<CustomerInfraPage />} />
              <Route path="servicios" element={<CustomerServicesPage />} />
              <Route path="descargas" element={<CustomerDownloadsPage />} />
              <Route path="soporte" element={<CustomerSupportPage />} />
              <Route path="soporte/:id" element={<CustomerSupportDetailPage />} />
              <Route path="perfil" element={<CustomerProfilePage />} />
            </Route>
            <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/" />} />
            <Route path="*" element={<Navigate to={isAuthenticated ? '/' : '/login'} />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default App;
